// ---------------------------------------------------------------------------
// AppLock — Password lifecycle management
// ---------------------------------------------------------------------------
import { deriveVerificationHash, deriveEncryptionKey, generateSalt, toBase64, fromBase64, timingSafeEqual, } from './key-derivation.js';
const STORAGE_KEY_SALT = 'applock_salt';
const STORAGE_KEY_HASH = 'applock_hash';
const STORAGE_KEY_CONFIG = 'applock_config';
/**
 * Manages password setup, verification, change, and removal.
 *
 * The password itself is never stored. Only the PBKDF2-derived
 * verification hash (with a unique salt) is persisted.
 */
export class PasswordManager {
    constructor(iterations) {
        this.iterations = iterations;
    }
    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------
    /** Whether a password has been configured. */
    isConfigured() {
        return localStorage.getItem(STORAGE_KEY_HASH) !== null;
    }
    /**
     * First-time password setup.
     * Generates a fresh salt, derives the verification hash, and stores both.
     * Returns the encryption key (for Tier 2 callers to hold in memory).
     */
    async setup(password) {
        if (this.isConfigured()) {
            throw new Error('[AppLock] Password already configured. Use changePassword() instead.');
        }
        const salt = generateSalt(16);
        const hash = await deriveVerificationHash(password, salt, this.iterations);
        const key = await deriveEncryptionKey(password, salt, this.iterations);
        const record = {
            salt: toBase64(salt),
            hash: toBase64(hash),
            iterations: this.iterations,
            version: 1,
        };
        localStorage.setItem(STORAGE_KEY_HASH, JSON.stringify(record));
        return key;
    }
    /**
     * Verify a password attempt.
     * Returns the derived encryption key on success, or null on failure.
     */
    async verify(password) {
        const record = this.getRecord();
        if (!record) {
            throw new Error('[AppLock] No password configured.');
        }
        const salt = fromBase64(record.salt);
        const storedHash = fromBase64(record.hash);
        const attemptHash = await deriveVerificationHash(password, salt, record.iterations);
        if (!timingSafeEqual(storedHash.buffer, attemptHash)) {
            return null;
        }
        // Password matches — derive the encryption key
        return deriveEncryptionKey(password, salt, record.iterations);
    }
    /**
     * Change the password. Returns the new encryption key.
     *
     * The caller is responsible for re-encrypting stored data with the
     * new key before this method replaces the stored hash.
     */
    async changePassword(currentPassword, newPassword) {
        // Verify current password
        const oldKey = await this.verify(currentPassword);
        if (!oldKey) {
            throw new Error('[AppLock] Current password is incorrect.');
        }
        // Generate new salt and derive new hash/key
        const newSalt = generateSalt(16);
        const newHash = await deriveVerificationHash(newPassword, newSalt, this.iterations);
        const newKey = await deriveEncryptionKey(newPassword, newSalt, this.iterations);
        // Update stored record
        const record = {
            salt: toBase64(newSalt),
            hash: toBase64(newHash),
            iterations: this.iterations,
            version: 1,
        };
        localStorage.setItem(STORAGE_KEY_HASH, JSON.stringify(record));
        return { oldKey, newKey };
    }
    /**
     * Remove the password entirely.
     * Returns the current encryption key so the caller can decrypt data
     * back to cleartext before completing removal.
     */
    async remove(currentPassword) {
        const key = await this.verify(currentPassword);
        if (!key) {
            throw new Error('[AppLock] Current password is incorrect.');
        }
        localStorage.removeItem(STORAGE_KEY_HASH);
        localStorage.removeItem(STORAGE_KEY_SALT);
        localStorage.removeItem(STORAGE_KEY_CONFIG);
        return key;
    }
    // -----------------------------------------------------------------------
    // Internal
    // -----------------------------------------------------------------------
    getRecord() {
        const raw = localStorage.getItem(STORAGE_KEY_HASH);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch {
            console.error('[AppLock] Corrupted password record.');
            return null;
        }
    }
}
//# sourceMappingURL=password-manager.js.map